// SignupFlow.js
import React, { useState } from 'react';
import { signupCompany, validateEmail } from './signupService';
import { getPasswordError } from './passwordValidation';
import AddressAutocomplete from './AddressAutocomplete';
import Modal from './components/Modal';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';
import { RADIUS, SHADOW, COLORS, labelStyle as themeLabelStyle, inputStyle as themeInputStyle } from './theme';
import Button from './components/Button';
import { formatPhoneNumber } from './phoneFormat';

const inputStyle = themeInputStyle({ fontSize: '16px' });
const labelStyle = themeLabelStyle();

// The one-and-done signup form — a real person's name/email/password plus their new company's
// basic info, all in a single step rather than the traditional multi-page wizard (account, then
// company, then a separate "review terms" page): everything here maps to exactly one Cloud
// Function call (functions/companySignup.js), so there's no meaningful intermediate state worth
// splitting across screens. Terms/Privacy are shown in a Modal rather than a link to a separate
// page — this app has no client-side router, so there's no real URL to send someone to without
// losing what they've already typed here.
function SignupFlow({ onBackToLogin, onSignupComplete }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyShortName, setCompanyShortName] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) return setError('Your name is required.');
    if (!validateEmail(email)) return setError('Enter a valid email address.');
    const passwordError = getPasswordError(password);
    if (passwordError) return setError(passwordError);
    if (password !== confirmPassword) return setError('Passwords don\'t match.');
    if (!companyName.trim()) return setError('Your company name is required.');
    if (!companyShortName.trim()) return setError('A short name for your company is required.');
    if (!tosAccepted) return setError('You need to agree to the Terms of Service and Privacy Policy to continue.');

    setSaving(true);
    try {
      const { user } = await signupCompany({
        name: name.trim(),
        email: email.trim(),
        password,
        companyName: companyName.trim(),
        companyShortName: companyShortName.trim(),
        companyPhone: companyPhone.trim(),
        companyAddress: companyAddress.trim()
      });
      onSignupComplete(user, ['admin']);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: COLORS.rowHover, padding: '24px 0' }}>
      <div style={{ backgroundColor: COLORS.white, padding: '40px', borderRadius: RADIUS.md, boxShadow: SHADOW.raised, width: '100%', maxWidth: '480px' }}>
        <div style={{ marginBottom: '28px', textAlign: 'center' }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', color: COLORS.primary, fontWeight: '700', letterSpacing: '1px' }}>TaskWRX</h1>
          <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>Set up your company account</p>
        </div>

        <form onSubmit={handleSubmit} data-testid="signup-form" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Your Name *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} data-testid="signup-name" placeholder="Jane Smith" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Your Email *</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signup-email" placeholder="jane@yourcompany.com" style={inputStyle} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Password *</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signup-password" placeholder="Min 6 chars, 1 capital, 1 number, 1 symbol" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Confirm Password *</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="signup-confirm-password" placeholder="Re-enter password" style={inputStyle} />
            </div>
          </div>

          <div style={{ borderTop: `1px solid ${COLORS.border}`, margin: '4px 0' }} />

          <div>
            <label style={labelStyle}>Company Name *</label>
            <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} data-testid="signup-company-name" placeholder="Your company's full name" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Short Name *</label>
            <input type="text" value={companyShortName} onChange={(e) => setCompanyShortName(e.target.value)} data-testid="signup-company-short-name" placeholder="Short abbreviation" maxLength={8} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Company Phone</label>
            <input type="tel" value={companyPhone} onChange={(e) => setCompanyPhone(formatPhoneNumber(e.target.value))} data-testid="signup-company-phone" placeholder="(555) 123-4567" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Company Address</label>
            <AddressAutocomplete
              name="companyAddress"
              value={companyAddress}
              onChange={setCompanyAddress}
              onSelect={(parsed) => setCompanyAddress(parsed.fullAddress || companyAddress)}
              placeholder="Start typing an address…"
              style={inputStyle}
            />
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12px', color: COLORS.textMuted }}>
            <input type="checkbox" checked={tosAccepted} onChange={(e) => setTosAccepted(e.target.checked)} style={{ marginTop: '2px' }} />
            <span>
              I agree to the{' '}
              <button type="button" onClick={() => setShowTerms(true)} style={{ padding: 0, background: 'none', border: 'none', color: COLORS.primary, fontWeight: '600', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' }}>
                Terms of Service
              </button>{' '}
              and{' '}
              <button type="button" onClick={() => setShowPrivacy(true)} style={{ padding: 0, background: 'none', border: 'none', color: COLORS.primary, fontWeight: '600', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' }}>
                Privacy Policy
              </button>
            </span>
          </label>

          {error && (
            <div style={{ padding: '12px', backgroundColor: COLORS.dangerBg, color: COLORS.danger, borderRadius: RADIUS.md, fontSize: '12px', border: `1px solid ${COLORS.dangerBorder}` }}>
              {error}
            </div>
          )}

          <Button type="submit" disabled={saving} data-testid="signup-submit">
            {saving ? 'Creating your account…' : 'Create Company Account'}
          </Button>

          <button
            type="button"
            onClick={onBackToLogin}
            disabled={saving}
            style={{ padding: '8px', backgroundColor: 'transparent', color: COLORS.primary, border: 'none', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
          >
            Already have an account? Sign in
          </button>
        </form>
      </div>

      {showTerms && (
        <Modal title="Terms of Service" onClose={() => setShowTerms(false)} maxWidth="lg" zIndex="modalTop">
          <TermsOfService />
        </Modal>
      )}
      {showPrivacy && (
        <Modal title="Privacy Policy" onClose={() => setShowPrivacy(false)} maxWidth="lg" zIndex="modalTop">
          <PrivacyPolicy />
        </Modal>
      )}
    </div>
  );
}

export default SignupFlow;
